import { useEffect, useMemo, useRef, useState } from 'react';
import type { Feed, UIComponent } from '~/services/layout/index.ts';
import { getLoadingSkeletonFeed } from '~/services/layout/layout';
import { useClientFeedApiEndpoint } from '~/hooks/use-client-feed-api-endpoint';
import { useClientFeed } from '~/hooks/use-client-feed';
import { isEnableDataRefreshing } from '~/services/ui-components';
import { useSubscribeToActionRefreshComponent } from '~/hooks/use-subscribe-to-action-refreshComponent';
import { useLoaderData } from '@remix-run/react';
import type { loader as rootLoader } from '~/routes/_index.tsx';
import { QUEUE_CHANGED_EVENT } from '~/services/queue/index.ts';
import { isQueueActionSource } from '~/services/ui-components/is-queue-action-source.ts';
import { uiComponentRequiresRemoteClientContext } from '~/services/plugins/zapp-context-setter/utils/index.ts';

function getFeedEntryCount(feedToCount: Feed | null | undefined): number {
  return feedToCount?.entry?.length ?? 0;
}

export type ClientFeedWrapperRenderProps = {
  feed: Feed | null | undefined;
  isClient: boolean;
  isLoading: boolean;
};

export type ClientFeedWrapperProps = {
  uiComponent: UIComponent;
  feed?: Feed | null | undefined;
  forceFresh?: boolean;
  render: (props: ClientFeedWrapperRenderProps) => JSX.Element;
};

/**
 * Handles fetching and refreshing feed data on the client while supporting SSR-provided data.
 */
export function ClientFeedWrapper({
  uiComponent,
  feed,
  forceFresh,
  render,
}: ClientFeedWrapperProps): JSX.Element {
  const { isTabsScreen } = useLoaderData<typeof rootLoader>();

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchDataRef = useRef<((forceFresh?: boolean) => Promise<void>) | null>(
    null
  );
  const lastFetchedEndpointRef = useRef<string | null>(null);
  const previousClientFeedApiEndpointRef = useRef<string | null>(null);

  const [isClient, setIsClient] = useState<boolean>(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const uiComponentDataSource: string | undefined = uiComponent?.data?.source;
  const isServerLoadedFeed: boolean = feed !== undefined;
  const hasServerProvidedFeedEntries: boolean = Boolean(feed?.entry?.length);
  const isObserveStorageComponent: boolean =
    uiComponentRequiresRemoteClientContext(uiComponent);
  const shouldApplyClientContext: boolean =
    isObserveStorageComponent || !isServerLoadedFeed;

  const {
    clientFeedApiEndpoint,
    hasClientContextInRequest,
    feedRequiresClientContext,
    hasContextSetterResolved,
  } = useClientFeedApiEndpoint({
    uiComponent,
    applyClientContext: shouldApplyClientContext,
  });

  const shouldFetchObserveStorageWhenSSRFeedEmpty: boolean =
    isObserveStorageComponent &&
    isServerLoadedFeed &&
    !hasServerProvidedFeedEntries;

  const shouldWaitForClientContextBeforeFetch: boolean =
    feedRequiresClientContext &&
    !hasClientContextInRequest &&
    !hasContextSetterResolved &&
    !shouldFetchObserveStorageWhenSSRFeedEmpty;
  const isWaitingForClientContext: boolean =
    shouldWaitForClientContextBeforeFetch && !hasServerProvidedFeedEntries;

  const { feedState, fetchData } = useClientFeed(clientFeedApiEndpoint, {
    uiComponentId: uiComponent.id,
    initialData:
      (feedRequiresClientContext ||
        shouldFetchObserveStorageWhenSSRFeedEmpty) &&
      !hasServerProvidedFeedEntries
        ? null
        : feed ?? null,
  });

  // sync fetchData ref, prevents re-rendering effects when fetchData changes
  fetchDataRef.current = fetchData;

  // sync feedState ref, prevents re-rendering effects when feedState changes
  const feedStateRef = useRef(feedState);
  feedStateRef.current = feedState;

  const shouldRefreshOnMount: boolean = useSubscribeToActionRefreshComponent({
    clientFeedApiEndpoint,
    fetchData,
    uiComponent,
  });

  useEffect(() => {
    if (!isQueueActionSource(uiComponentDataSource)) {
      return;
    }

    const onQueueChanged = () => {
      if (fetchDataRef.current) {
        void fetchDataRef.current(true);
      }
    };

    window.addEventListener(QUEUE_CHANGED_EVENT, onQueueChanged);

    return () => {
      window.removeEventListener(QUEUE_CHANGED_EVENT, onQueueChanged);
    };
  }, [uiComponentDataSource]);

  const refreshInterval = uiComponent?.rules?.refreshing_interval;
  const hasRefreshEnabled: boolean =
    isEnableDataRefreshing(uiComponent) && !!refreshInterval;

  // Refetch when the client feed endpoint changes (e.g. context tags applied).
  useEffect(() => {
    if (!uiComponentDataSource || !clientFeedApiEndpoint) return;

    const previousEndpoint: string | null =
      previousClientFeedApiEndpointRef.current;

    previousClientFeedApiEndpointRef.current = clientFeedApiEndpoint;

    if (
      previousEndpoint === null ||
      previousEndpoint === clientFeedApiEndpoint
    ) {
      return;
    }

    if (fetchDataRef.current) {
      lastFetchedEndpointRef.current = clientFeedApiEndpoint;
      void fetchDataRef.current(true);
    }
  }, [
    clientFeedApiEndpoint,
    uiComponentDataSource,
    hasClientContextInRequest,
    hasServerProvidedFeedEntries,
  ]);

  // Initial fetch - when endpoint changes or when we don't have data
  useEffect(() => {
    if (shouldWaitForClientContextBeforeFetch) return;

    if (
      !uiComponentDataSource ||
      !clientFeedApiEndpoint ||
      feedStateRef.current.state !== 'idle' ||
      lastFetchedEndpointRef.current === clientFeedApiEndpoint ||
      (!isTabsScreen &&
        !shouldRefreshOnMount &&
        (feedStateRef.current.data ||
          feedStateRef.current.stableData ||
          hasServerProvidedFeedEntries) &&
        !hasClientContextInRequest &&
        !feedRequiresClientContext &&
        !shouldFetchObserveStorageWhenSSRFeedEmpty)
    ) {
      return;
    }

    lastFetchedEndpointRef.current = clientFeedApiEndpoint;
    if (fetchDataRef.current) {
      fetchDataRef.current(!!forceFresh || shouldRefreshOnMount);
    }
  }, [
    uiComponentDataSource,
    clientFeedApiEndpoint,
    forceFresh,
    isTabsScreen,
    hasClientContextInRequest,
    feedRequiresClientContext,
    hasServerProvidedFeedEntries,
    shouldWaitForClientContextBeforeFetch,
    shouldFetchObserveStorageWhenSSRFeedEmpty,
    shouldRefreshOnMount,
  ]);

  // Interval for refreshing
  useEffect(() => {
    if (
      !hasRefreshEnabled ||
      !clientFeedApiEndpoint ||
      shouldWaitForClientContextBeforeFetch
    ) {
      return;
    }

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      // Hook's isLoadingRef guard will prevent concurrent requests
      if (fetchDataRef.current) {
        fetchDataRef.current(true);
      }
    }, refreshInterval * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    hasRefreshEnabled,
    clientFeedApiEndpoint,
    refreshInterval,
    shouldWaitForClientContextBeforeFetch,
  ]);

  const loadingSkeletonFeed: Feed = useMemo(
    () => getLoadingSkeletonFeed({ idPrefix: uiComponent.id }),
    [uiComponent.id]
  );

  const feedToRender = useMemo(() => {
    if (isWaitingForClientContext) {
      return loadingSkeletonFeed;
    }

    const resolvedClientFeed: Feed | null | undefined =
      feedState.state === 'loading'
        ? feedState.stableData ?? feedState.data
        : feedState.data ?? feedState.stableData;

    const clientFeedForCurrentContext: Feed | null | undefined =
      feedRequiresClientContext &&
      feedState.state === 'loading' &&
      !hasClientContextInRequest
        ? undefined
        : resolvedClientFeed;

    let resolvedFeed: Feed | null | undefined;

    if (feedRequiresClientContext) {
      const clientEntryCount: number = getFeedEntryCount(
        clientFeedForCurrentContext
      );

      if (clientEntryCount > 0) {
        resolvedFeed = clientFeedForCurrentContext;
      } else if (
        hasServerProvidedFeedEntries &&
        (!clientFeedForCurrentContext || feedState.state === 'loading')
      ) {
        resolvedFeed = feed;
      } else {
        resolvedFeed = clientFeedForCurrentContext;
      }
    } else {
      const serverEntryCount: number = getFeedEntryCount(feed);
      const clientEntryCount: number = getFeedEntryCount(
        clientFeedForCurrentContext
      );

      const shouldUseServerFeedWhileLoading: boolean =
        feedState.state === 'loading' &&
        serverEntryCount > 0 &&
        clientEntryCount === 0;

      if (shouldUseServerFeedWhileLoading) {
        resolvedFeed = feed;
      } else if (hasServerProvidedFeedEntries && !clientFeedForCurrentContext) {
        resolvedFeed = feed;
      } else {
        resolvedFeed = clientFeedForCurrentContext ?? feed;
      }
    }

    if (
      getFeedEntryCount(resolvedFeed) === 0 &&
      feedState.state === 'loading'
    ) {
      return loadingSkeletonFeed;
    }

    return resolvedFeed;
  }, [
    feedState.data,
    feedState.stableData,
    feedState.state,
    feed,
    feedRequiresClientContext,
    hasClientContextInRequest,
    hasServerProvidedFeedEntries,
    isWaitingForClientContext,
    loadingSkeletonFeed,
  ]);

  const isLoading = useMemo(() => {
    if (isWaitingForClientContext) {
      return true;
    }

    const hasExistingFeedEntries: boolean =
      getFeedEntryCount(feedState.stableData) > 0;

    if (
      hasRefreshEnabled &&
      feedState.state === 'loading' &&
      hasExistingFeedEntries
    ) {
      return false;
    }

    return hasRefreshEnabled && !feedState.data && feedState.stableData
      ? false
      : hasRefreshEnabled
      ? feedState.state !== 'idle'
      : feedState.state !== 'idle' && !feedState.data;
  }, [
    hasRefreshEnabled,
    feedState.data,
    feedState.stableData,
    feedState.state,
    isWaitingForClientContext,
  ]);

  const renderProps = useMemo<ClientFeedWrapperRenderProps>(
    () => ({
      feed: feedToRender,
      isClient,
      isLoading,
    }),
    [feedToRender, isClient, isLoading]
  );

  return render(renderProps);
}
